"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseSharedDataVariant = parseSharedDataVariant;
exports.parseSharedDataQueryString = parseSharedDataQueryString;
exports.injectedSharedVariableName = injectedSharedVariableName;
exports.prepareScriptCodeWithInjectedVariables = prepareScriptCodeWithInjectedVariables;
exports.isDeclaredSharedVariable = isDeclaredSharedVariable;
exports.validateSharedVariablesAndSharedData = validateSharedVariablesAndSharedData;
exports.findSharedVariableDeclaration = findSharedVariableDeclaration;
const colorPickerRichText_1 = require("./colorPickerRichText");
const SHARED_DATA_QUERY_PART = /^\s*([a-z][a-z0-9_]*)\s*:\s*(total|count|list|map)\s*$/i;
const SHARED_INJECTED_VAR = /\$\{shared_([a-z][a-z0-9_]*)_(total|count|list|map)\}/g;
const SHARED_DATA_VARIANTS = new Set(['card', 'plain']);
function parseSharedDataVariant(raw) {
    const trimmed = String(raw ?? '').trim().replace(/^"|"$/g, '');
    if (!trimmed) {
        return 'card';
    }
    if (SHARED_DATA_VARIANTS.has(trimmed)) {
        return trimmed;
    }
    throw new Error(`Invalid Shared-Data variant '${trimmed}'. Expected 'card' or 'plain'`);
}
function parseSharedDataQueryString(query) {
    const trimmed = query.trim();
    if (!trimmed) {
        throw new Error('Shared-Data query attribute cannot be empty');
    }
    const specs = [];
    for (const part of trimmed.split(',')) {
        const piece = part.trim();
        if (!piece) {
            continue;
        }
        const match = piece.match(SHARED_DATA_QUERY_PART);
        if (!match) {
            throw new Error(`Invalid Shared-Data query entry '${piece}'. Expected format: variable_name:total|count|list|map`);
        }
        specs.push({
            variableName: match[1],
            aggregation: match[2].toLowerCase()
        });
    }
    if (specs.length === 0) {
        throw new Error('Shared-Data query attribute must include at least one variable_name:aggregation pair');
    }
    return specs;
}
function injectedSharedVariableName(variableName, aggregation) {
    return `shared_${variableName}_${aggregation}`;
}
/**
 * Substitutes ${name} placeholders in Shared-Data script blocks using injected
 * shared_* values. Quoted "${name}" patterns become JSON string literals so map/list
 * payloads remain valid JavaScript when embedded in script source strings.
 */
function prepareScriptCodeWithInjectedVariables(scriptCode, injectedVariables) {
    const resolveLiteral = (name) => {
        if (!Object.prototype.hasOwnProperty.call(injectedVariables, name)) {
            return undefined;
        }
        const value = injectedVariables[name];
        if (value === undefined || value === null) {
            return JSON.stringify('');
        }
        return JSON.stringify(String(value));
    };
    let prepared = scriptCode.replace(/"\$\{([a-zA-Z0-9_]+)\}"/g, (match, name) => {
        const literal = resolveLiteral(name);
        return literal ?? match;
    });
    prepared = prepared.replace(/\$\{([a-zA-Z0-9_]+)\}/g, (match, name) => {
        const literal = resolveLiteral(name);
        return literal ?? match;
    });
    return prepared;
}
function getNestedRichTextElements(element) {
    const out = [];
    if (element.type === 'conditional') {
        if (Array.isArray(element.children)) {
            out.push(...element.children);
        }
        if (Array.isArray(element.elseChildren)) {
            out.push(...element.elseChildren);
        }
        return out;
    }
    if (element.type === 'inline-option' && Array.isArray(element.content)) {
        out.push(...element.content);
        return out;
    }
    if (element.type === 'scroll-area' || element.type === 'shared-data') {
        if (Array.isArray(element.content)) {
            for (const item of element.content) {
                if (item && typeof item === 'object' && 'type' in item) {
                    out.push(item);
                }
            }
        }
        return out;
    }
    if (Array.isArray(element.content)) {
        for (const item of element.content) {
            if (item && typeof item === 'object' && 'type' in item) {
                out.push(item);
            }
        }
    }
    return out;
}
function collectInterpolationStringsFromElements(elements) {
    const strings = [];
    const walk = (elts) => {
        for (const el of elts) {
            if (el.type === 'text' && typeof el.content === 'string') {
                strings.push(el.content);
            }
            if (typeof el.content === 'string' && el.type !== 'text') {
                strings.push(el.content);
            }
            walk(getNestedRichTextElements(el));
        }
    };
    walk(elements);
    return strings;
}
function validateSharedDataElement(element, declaredNames, errors) {
    const attrs = element.sharedDataAttributes;
    if (!attrs) {
        errors.push('Shared-Data element is missing parsed query attributes');
        return;
    }
    const expectedInjected = new Set();
    for (const spec of attrs.queries) {
        if (!declaredNames.has(spec.variableName)) {
            errors.push(`Shared-Data query references undeclared shared variable '${spec.variableName}'`);
        }
        expectedInjected.add(injectedSharedVariableName(spec.variableName, spec.aggregation));
    }
    const childElements = Array.isArray(element.content)
        ? element.content
        : [];
    const interpolationSources = collectInterpolationStringsFromElements(childElements);
    for (const source of interpolationSources) {
        for (const match of source.matchAll(SHARED_INJECTED_VAR)) {
            const injectedName = `shared_${match[1]}_${match[2].toLowerCase()}`;
            if (!expectedInjected.has(injectedName)) {
                errors.push(`Warning: Shared-Data block uses \${${injectedName}} but that aggregate is not requested in query="${attrs.query}"`);
            }
        }
    }
}
function walkRichTextElements(elements, declaredNames, errors) {
    for (const el of elements) {
        if (el.type === 'shared-data') {
            validateSharedDataElement(el, declaredNames, errors);
        }
        walkRichTextElements(getNestedRichTextElements(el), declaredNames, errors);
    }
}
function collectAdventureRichTextFragments(adventure) {
    const parts = [adventure.description];
    for (const character of adventure.characters ?? []) {
        parts.push(character.description);
    }
    for (const scene of adventure.scenes ?? []) {
        for (const section of scene.sections ?? []) {
            parts.push(section.description);
            for (const opt of section.options ?? []) {
                parts.push(opt.text);
                parts.push(opt.waitMessage);
            }
        }
    }
    for (const item of adventure.items ?? []) {
        parts.push(item.description);
    }
    for (const location of adventure.locations ?? []) {
        parts.push(location.description);
    }
    return parts;
}
function isDeclaredSharedVariable(adventure, name) {
    return !!adventure?.sharedVariables?.some((d) => d.name === name);
}
function validateSharedVariablesAndSharedData(adventure) {
    const errors = [];
    const sharedVars = adventure.sharedVariables ?? [];
    const names = sharedVars.map((v) => v.name);
    const duplicateNames = names.filter((name, index) => names.indexOf(name) !== index);
    if (duplicateNames.length > 0) {
        errors.push(`Duplicate shared variable names: ${[...new Set(duplicateNames)].join(', ')}`);
    }
    for (const decl of sharedVars) {
        if (!['string', 'number', 'boolean'].includes(decl.type)) {
            errors.push(`Shared variable '${decl.name}' has invalid type '${decl.type}'. Shared variables support string, number, and boolean only`);
        }
    }
    const declaredNames = new Set(names);
    for (const fragment of collectAdventureRichTextFragments(adventure)) {
        const desc = (0, colorPickerRichText_1.toRichTextDescription)(fragment);
        if (!desc) {
            continue;
        }
        walkRichTextElements(desc.elements, declaredNames, errors);
    }
    return errors;
}
function findSharedVariableDeclaration(sharedVariables, name) {
    return sharedVariables?.find((d) => d.name === name);
}
//# sourceMappingURL=sharedDataRichText.js.map